import type { UnifiedPublicLoaderContext } from '~kui/composables/useUnifiedPublicLoaderContext';

const LOADER_SHOWN_KEY = 'unified-loader-shown';

export const useUnifiedPublicLoaderAnimation = (
  wrapperRef: Ref<HTMLElement | null>,
  overlayRef: Ref<HTMLElement | null>,
  logoRef: Ref<HTMLElement | null>,
  loaderCtx: UnifiedPublicLoaderContext | null,
) => {
  const { gsap, gsapContext } = useGsap(wrapperRef);
  onMounted(async () => {
    if (localStorage.getItem(LOADER_SHOWN_KEY) === 'true') {
      if (loaderCtx) {
        loaderCtx.loaderComplete.value = true;
        loaderCtx.loaderGone.value = true;
      }
      return;
    }

    localStorage.setItem(LOADER_SHOWN_KEY, 'true');

    // First visit only: hide the content for the duration of the intro. Set synchronously here
    // (before any await, i.e. before the first paint) so the server-rendered content — which is
    // visible by default for crawlers and no-JS — is hidden without a visible flash while the
    // overlay animates. It's revealed again when loaderComplete flips below.
    if (loaderCtx) {
      loaderCtx.loaderActive.value = true;
    }

    // nextTick ensures sibling components (header/nav) have mounted
    // so loaderCtx.navRef is populated before we set its initial GSAP state
    await nextTick();
    gsapContext(() => {
      document.body.style.overflow = 'hidden';
      const overlay = overlayRef.value;
      const logo = logoRef.value;
      const nav = loaderCtx?.navRef.value ?? null;

      if (!overlay || !logo) {
        return;
      }

      const svg = logo.querySelector('svg');
      if (!svg) {
        return;
      }

      // Natural height before any GSAP scale is applied.
      const naturalLogoHeight = logo.getBoundingClientRect().height || 64;

      // Anchor the loader logo's base position (x=0, y=0) at the header logo's exact
      // center. This means the fly-back animation is simply x→0, y→0 — pixel-perfect
      // by construction, no delta calculation needed. The nav registers its logo in the
      // loader context when it mounts.
      const headerLogo = loaderCtx?.logoRef.value ?? null;
      const hr = headerLogo?.getBoundingClientRect();
      const headerCX = hr ? hr.left + hr.width / 2 : window.innerWidth / 2;
      const headerCY = hr ? hr.top + hr.height / 2 : window.innerHeight / 2;
      const headerScale = hr ? hr.height / naturalLogoHeight : 1;

      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;

      // Base at header center; x/y offset positions it at viewport center initially.
      gsap.set(logo, {
        left: headerCX,
        top: headerCY,
        xPercent: -50,
        yPercent: -50,
        x: cx - headerCX,
        y: cy - headerCY + 20, // extra 20px down for bounce-in
        transformOrigin: '50% 50%',
        transformPerspective: 800,
        transformStyle: 'preserve-3d',
        opacity: 0,
        scale: 0.8,
      });
      if (nav) {
        gsap.set(nav, { opacity: 0, y: -16 });
      }

      // Inject bottom→top linearGradient into the SVG's existing defs.
      // viewBox is "0 0 81 20" — y=20 is bottom, y=0 is top.
      const ns = 'http://www.w3.org/2000/svg';
      const defs = svg.querySelector('defs')!;
      const gradId = 'unified-loader-logo-fill';
      const offBlack = 'rgb(35 35 35)'; // --foreground

      const grad = document.createElementNS(ns, 'linearGradient');
      grad.id = gradId;
      grad.setAttribute('gradientUnits', 'userSpaceOnUse');
      grad.setAttribute('x1', '0');
      grad.setAttribute('y1', '20');
      grad.setAttribute('x2', '0');
      grad.setAttribute('y2', '0');

      const s1 = document.createElementNS(ns, 'stop');
      s1.setAttribute('offset', '0');
      s1.setAttribute('stop-color', offBlack);

      const s2 = document.createElementNS(ns, 'stop');
      s2.setAttribute('offset', '0');
      s2.setAttribute('stop-color', offBlack);

      const s3 = document.createElementNS(ns, 'stop');
      s3.setAttribute('offset', '0.002');
      s3.setAttribute('stop-color', '#ffffff');

      const s4 = document.createElementNS(ns, 'stop');
      s4.setAttribute('offset', '1');
      s4.setAttribute('stop-color', '#ffffff');

      grad.append(s1, s2, s3, s4);
      defs.appendChild(grad);

      svg.querySelectorAll('path').forEach((p) => {
        (p as SVGPathElement).style.fill = `url(#${gradId})`;
      });

      const updateFill = (level: number) => {
        s2.setAttribute('offset', level.toFixed(4));
        s3.setAttribute('offset', Math.min(1, level + 0.002).toFixed(4));
      };
      updateFill(0);

      const coverScale = () =>
        (Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2) / 96) * 1.1;

      const tl = gsap.timeline();

      // Phase 1: Circle expands to cover screen
      tl.to(overlay, { scale: coverScale, opacity: 1, duration: 0.6, ease: 'power3.in' });

      // Phase 2: Logo settles in
      tl.to(logo, { opacity: 1, scale: 1.3, y: cy - headerCY, duration: 0.5, ease: 'back.out(1.7)', transformOrigin: '50% 50%' }, 0.5);

      // Phase 3: Fill black bottom → top
      const fillState = { level: 0 };
      tl.to(fillState, {
        level: 1,
        duration: 1.0,
        ease: 'power1.inOut',
        onUpdate: () => updateFill(fillState.level),
      }, 0.55);

      // Phase 4a: Zoom in
      tl.to(logo, { scale: 2, x: cx - headerCX, y: cy - headerCY, duration: 0.5, transformOrigin: '50% 50%', ease: 'expo.out' }, 1.8);

      // Phase 4b: Fly back to header logo position
      tl.to(logo, {
        x: 0,
        y: 0,
        scale: headerScale,
        z: 0,
        duration: 0.45,
        ease: 'power3.inOut',
        onComplete: () => {
          if (loaderCtx) {
            loaderCtx.loaderComplete.value = true;
          }
          if (nav) {
            gsap.to(nav, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out', delay: 0.2 });
          }
          gsap.delayedCall(0.3, () => {
            gsap.to(overlay, {
              scale: 0,
              duration: 0.4,
              ease: 'power3.inOut',
              onComplete: () => {
                if (loaderCtx) {
                  loaderCtx.loaderGone.value = true;
                  gsap.set(logo, { opacity: 0 });
                }
                document.body.style.overflow = '';
              },
            });
          });
        },
      }, 2.1);
    });
  });

  onUnmounted(() => {
    document.body.style.overflow = '';
  });
};
